export interface ShellRenderState {
  focusedTaskId: string | null;
  searchHadFocus: boolean;
  searchSelection: number | null;
  myDayFocus?: { search: boolean; selection: number | null; taskId?: string; pickerOffset: number };
  sidebarScroll: number;
  detailScroll: number;
  workspaceScroll: number;
  boardScroll: number | null;
  timelineScrollTop?: number | null;
  timelineScrollLeft?: number | null;
  timelineCollectionId?: string | null;
  tableScroll?: { scope: string; left: number; top: number };
  boardColumnScrolls: { collectionId: string; column: string; scrollTop: number }[];
  calendarScrollTop: number | null;
  calendarScrollLeft: number | null;
  taskPanelAlreadyOpen: boolean;
  taskMoreOpen: boolean;
  viewHelp?: { view: string; open: boolean; focused: boolean };
}

export interface ShellRenderRestoreCapabilities {
  document: Document;
  selected: boolean;
  restoreSidebar: (sidebar: HTMLElement, scrollTop: number) => void;
  syncCalendarHeaderScroll: (calendar: HTMLElement) => void;
  setTaskMoreOpen: (open: boolean) => void;
  updateBackToTopVisibility: () => void;
  enableBackGuard: () => void;
  requestFrame: (callback: FrameRequestCallback) => number;
  restoreWorkspaceScroll: (scrollTop: number) => void;
}

export function retainActionableToasts(document: Document): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>("#toast-region .toast--action"));
}

export function focusTimelineAnchor(scroll: HTMLElement): void {
  const offset = Number(scroll.dataset.timelineFocusOffset);
  if (!Number.isFinite(offset)) return;
  const nameWidth = scroll.querySelector<HTMLElement>(".collection-timeline__name")?.getBoundingClientRect().width || 150;
  const visibleWidth = Math.max(36, scroll.clientWidth - nameWidth);
  scroll.scrollLeft = Math.max(0, offset - Math.max(0, (visibleWidth - 36) / 2));
  scroll.scrollTop = 0;
}

export function restoreActionableToasts(document: Document, toasts: HTMLElement[]): void {
  document.querySelector("#toast-region")?.append(...toasts);
}

export function captureShellRenderState(document: Document, workspaceScroll: number, fallbackTaskMoreOpen: boolean): ShellRenderState {
  const activeElement = document.activeElement;
  const focusedTaskId = activeElement instanceof Element ? activeElement.closest<HTMLElement>("[data-todo]")?.dataset.todo || null : null;
  const searchHadFocus = activeElement instanceof HTMLInputElement && activeElement.id === "search";
  const myDaySearch = activeElement instanceof HTMLInputElement && activeElement.id === "my-day-search";
  const myDayTaskId = activeElement?.getAttribute("data-my-day-toggle") || undefined;
  const viewHelp = document.querySelector<HTMLDetailsElement>("[data-view-help]");
  const table = document.querySelector<HTMLElement>(".task-table-scroll");

  return {
    focusedTaskId,
    ...(table ? { tableScroll: { scope: table.closest<HTMLElement>("[data-table-scope]")?.dataset.tableScope || "", left: table.scrollLeft, top: table.scrollTop } } : {}),
    viewHelp: viewHelp ? { view: viewHelp.dataset.viewHelp || "", open: viewHelp.open, focused: activeElement === viewHelp.querySelector("summary") } : undefined,
    searchHadFocus,
    searchSelection: searchHadFocus ? activeElement.selectionStart : null,
    myDayFocus: myDaySearch || myDayTaskId ? { search: myDaySearch, selection: myDaySearch ? (activeElement as HTMLInputElement).selectionStart : null, taskId: myDayTaskId, pickerOffset: (document.querySelector("[data-my-day-picker]")?.getBoundingClientRect().top || 0) + workspaceScroll } : undefined,
    sidebarScroll: document.querySelector<HTMLElement>("#sidebar")?.scrollTop || 0,
    detailScroll: document.querySelector<HTMLElement>(".detail-panel__scroll")?.scrollTop || 0,
    workspaceScroll,
    boardScroll: document.querySelector<HTMLElement>(".kanban-board")?.scrollLeft ?? null,
    timelineScrollTop: document.querySelector<HTMLElement>(".collection-timeline__scroll")?.scrollTop ?? null,
    timelineScrollLeft: document.querySelector<HTMLElement>(".collection-timeline__scroll")?.scrollLeft ?? null,
    timelineCollectionId: document.querySelector<HTMLElement>(".collection-timeline")?.dataset.timelineCollection ?? null,
    boardColumnScrolls: Array.from(document.querySelectorAll<HTMLElement>(".kanban-column")).map(item => ({ collectionId: item.dataset.boardCollection || "", column: item.dataset.boardColumn || "", scrollTop: item.querySelector<HTMLElement>(".kanban-cards")?.scrollTop || 0 })),
    calendarScrollTop: document.querySelector<HTMLElement>(".calendar-time-scroll")?.scrollTop ?? null,
    calendarScrollLeft: document.querySelector<HTMLElement>(".calendar-time-scroll")?.scrollLeft ?? null,
    taskPanelAlreadyOpen: !!document.querySelector(".detail-panel"),
    taskMoreOpen: document.querySelector<HTMLDetailsElement>(".task-more-details")?.open ?? fallbackTaskMoreOpen,
  };
}

export function restoreShellRenderState(state: ShellRenderState, capabilities: ShellRenderRestoreCapabilities): void {
  const { document } = capabilities;
  if (state.viewHelp) {
    const help = document.querySelector<HTMLDetailsElement>("[data-view-help]");
    if (help?.dataset.viewHelp === state.viewHelp.view) {
      help.open = state.viewHelp.open;
      if (state.viewHelp.focused && !capabilities.selected) help.querySelector<HTMLElement>("summary")?.focus({ preventScroll: true });
    }
  }
  const sidebar = document.querySelector<HTMLElement>("#sidebar");
  const detail = document.querySelector<HTMLElement>(".detail-panel__scroll");
  const openingDetailPanel = !state.taskPanelAlreadyOpen ? document.querySelector<HTMLElement>(".detail-panel") : null;

  if (openingDetailPanel) {
    openingDetailPanel.classList.add("is-preparing");
    capabilities.requestFrame(() => capabilities.requestFrame(() => {
      openingDetailPanel.classList.remove("is-preparing");
      openingDetailPanel.classList.add("is-entering");
      openingDetailPanel.addEventListener("animationend", event => {
        if (event.target === openingDetailPanel && event.animationName === "panel-in") openingDetailPanel.classList.remove("is-entering");
      });
    }));
  }

  if (sidebar) capabilities.restoreSidebar(sidebar, state.sidebarScroll);
  if (detail) detail.scrollTop = state.detailScroll;
  if (state.boardScroll !== null) {
    const board = document.querySelector<HTMLElement>(".kanban-board");
    if (board) board.scrollLeft = state.boardScroll;
  }
  const timeline = document.querySelector<HTMLElement>(".collection-timeline__scroll");
  const table = document.querySelector<HTMLElement>(".task-table-scroll");
  if (table && state.tableScroll?.scope === table.closest<HTMLElement>("[data-table-scope]")?.dataset.tableScope) {
    table.scrollLeft = state.tableScroll?.left || 0;
    table.scrollTop = state.tableScroll?.top || 0;
  }
  if (timeline) {
    if (state.timelineCollectionId && state.timelineCollectionId === timeline.closest<HTMLElement>(".collection-timeline")?.dataset.timelineCollection) {
      timeline.scrollTop = state.timelineScrollTop ?? 0;
      timeline.scrollLeft = state.timelineScrollLeft ?? 0;
    } else focusTimelineAnchor(timeline);
  }
  for (const saved of state.boardColumnScrolls) {
    const column = Array.from(document.querySelectorAll<HTMLElement>(".kanban-column")).find(item => item.dataset.boardCollection === saved.collectionId && item.dataset.boardColumn === saved.column);
    const cards = column?.querySelector<HTMLElement>(".kanban-cards");
    if (cards) cards.scrollTop = saved.scrollTop;
  }

  const calendar = document.querySelector<HTMLElement>(".calendar-time-scroll");
  if (calendar) {
    calendar.scrollTop = state.calendarScrollTop ?? 7 * 48;
    calendar.scrollLeft = state.calendarScrollLeft ?? 0;
    capabilities.syncCalendarHeaderScroll(calendar);
    calendar.addEventListener("scroll", () => capabilities.syncCalendarHeaderScroll(calendar), { passive: true });
  }

  if (capabilities.selected || state.taskPanelAlreadyOpen || state.boardScroll !== null || state.timelineScrollLeft != null || state.tableScroll || state.myDayFocus && document.querySelector("[data-my-day-picker]")) {
    capabilities.requestFrame(() => {
      const picker = !capabilities.selected && state.myDayFocus ? document.querySelector("[data-my-day-picker]") : null;
      const offset = picker && state.myDayFocus ? picker.getBoundingClientRect().top + (document.defaultView?.scrollY || 0) - state.myDayFocus.pickerOffset : 0;
      capabilities.restoreWorkspaceScroll(state.workspaceScroll + offset);
    });
  }
  if (state.taskMoreOpen) {
    const more = document.querySelector<HTMLDetailsElement>(".task-more-details");
    if (more) {
      more.open = true;
      capabilities.setTaskMoreOpen(true);
    }
  }
  if (state.myDayFocus && !capabilities.selected) {
    const search = document.querySelector<HTMLInputElement>("#my-day-search");
    const button = Array.from(document.querySelectorAll<HTMLElement>("[data-my-day-toggle]")).find(item => item.dataset.myDayToggle === state.myDayFocus?.taskId);
    const target = state.myDayFocus.search ? search : button || search;
    target?.focus({ preventScroll: true });
    if (state.myDayFocus.search && state.myDayFocus.selection !== null) search?.setSelectionRange(state.myDayFocus.selection, state.myDayFocus.selection);
  } else if (state.searchHadFocus) {
    const search = document.querySelector<HTMLInputElement>("#search");
    search?.focus();
    if (state.searchSelection !== null) search?.setSelectionRange(state.searchSelection, state.searchSelection);
  } else if (state.focusedTaskId && !capabilities.selected) {
    document.querySelector<HTMLElement>(`[data-todo="${state.focusedTaskId}"]`)?.focus({ preventScroll: true });
  }

  capabilities.updateBackToTopVisibility();
  capabilities.enableBackGuard();
}
